import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Container, Button, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { PasskeyService, Passkey } from '../services/PasskeyService';

const AccountSettingsPage: React.FC = () => {
  const { isAuthenticated, loading, passkeyAvailable } = useAuth();
  const navigate = useNavigate();
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [loadingPasskeys, setLoadingPasskeys] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [selectedPasskey, setSelectedPasskey] = useState<Passkey | null>(null);
  const [newName, setNewName] = useState('');
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, loading, navigate]);

  const loadPasskeys = useCallback(async () => {
    if (!passkeyAvailable) { setLoadingPasskeys(false); return; }
    try {
      const list = await PasskeyService.listPasskeys();
      setPasskeys(list);
    } catch {
      setMessage({ text: 'Failed to load passkeys', severity: 'error' });
    } finally {
      setLoadingPasskeys(false);
    }
  }, [passkeyAvailable]);

  useEffect(() => { loadPasskeys(); }, [loadPasskeys]);

  const handleRegister = async () => {
    setRegistering(true);
    const result = await PasskeyService.registerPasskey();
    setRegistering(false);
    setMessage({ text: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) loadPasskeys();
  };

  const handleDelete = async (id: number) => {
    const result = await PasskeyService.deletePasskey(id);
    setMessage({ text: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) loadPasskeys();
  };

  const handleRename = async () => {
    if (!selectedPasskey || !newName.trim()) return;
    const result = await PasskeyService.renamePasskey(selectedPasskey.id, newName.trim());
    setMessage({ text: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) loadPasskeys();
    setRenameDialogOpen(false);
  };

  if (loading) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4, maxWidth: 700 }}>
        <Typography variant="h4" gutterBottom color="primary">Account Settings</Typography>

        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Passkeys</Typography>
          {!passkeyAvailable ? (
            <Typography variant="body2" color="text.secondary">
              Passkeys are not supported in this browser.
            </Typography>
          ) : loadingPasskeys ? (
            <CircularProgress size={24} />
          ) : (
            <>
              {passkeys.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No passkeys registered. Add one for faster sign-in.
                </Typography>
              ) : (
                <List>
                  {passkeys.map(pk => (
                    <ListItem key={pk.id}>
                      <FingerprintIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <ListItemText
                        primary={pk.friendly_name || `Passkey ${pk.id}`}
                        secondary={`Added ${new Date(pk.created_at).toLocaleDateString()}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton onClick={() => { setSelectedPasskey(pk); setNewName(pk.friendly_name || ''); setRenameDialogOpen(true); }}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDelete(pk.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
              <Button variant="outlined" color="primary" onClick={handleRegister} disabled={registering}
                      startIcon={registering ? <CircularProgress size={20} /> : <FingerprintIcon />} sx={{ mt: 1 }}>
                {registering ? 'Registering...' : 'Add Passkey'}
              </Button>
            </>
          )}
        </Paper>
      </Container>
      <Footer />

      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Rename Passkey</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Name" value={newName} onChange={e => setNewName(e.target.value)} margin="dense" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRename} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!message} autoHideDuration={4000} onClose={() => setMessage(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setMessage(null)} severity={message?.severity || 'info'}>{message?.text}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettingsPage;
